import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Language, getTranslation, formatDate, formatTime, formatDateTime, formatNumber, formatCurrency, formatWeight } from './index';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  formatDate: (date: Date | string, format?: 'short' | 'long' | 'full') => string;
  formatTime: (date: Date | string, includeSeconds?: boolean) => string;
  formatDateTime: (date: Date | string) => string;
  formatNumber: (num: number, decimals?: number) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatWeight: (kg: number) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'app_language';

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  // Initialize language from localStorage or browser settings
  const getInitialLanguage = (): Language => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'zh-TW' || stored === 'es') {
      return stored;
    }

    // Check browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('zh')) {
      return 'zh-TW';
    }
    if (browserLang.startsWith('es')) {
      return 'es';
    }

    return 'en';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Map Language to HTML lang attribute
  const getHtmlLang = (lang: Language): string => {
    const langMap: Record<Language, string> = {
      en: 'en',
      'zh-TW': 'zh-TW',
      es: 'es',
    };
    return langMap[lang];
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

    // Update HTML lang attribute for accessibility and SEO
    document.documentElement.lang = getHtmlLang(lang);
  };

  // Set initial HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = getHtmlLang(language);
  }, [language]);

  // Create context value with bound functions - memoized to update when language changes
  const contextValue: LanguageContextType = useMemo(() => ({
    language,
    setLanguage,
    t: (key: string): string => getTranslation(language, key),
    formatDate: (date: Date | string, format: 'short' | 'long' | 'full' = 'short') => formatDate(date, language, format),
    formatTime: (date: Date | string, includeSeconds = false) => formatTime(date, language, includeSeconds),
    formatDateTime: (date: Date | string) => formatDateTime(date, language),
    formatNumber: (num: number, decimals = 2) => formatNumber(num, language, decimals),
    formatCurrency: (amount: number, currency = 'USD') => formatCurrency(amount, language, currency),
    formatWeight: (kg: number) => formatWeight(kg, language),
  }), [language]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

// Fallback context for when useLanguage is called outside LanguageProvider (e.g. during HMR)
const fallbackContext: LanguageContextType = {
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => getTranslation('en', key),
  formatDate: (date: Date | string, format: 'short' | 'long' | 'full' = 'short') => formatDate(date, 'en', format),
  formatTime: (date: Date | string, includeSeconds = false) => formatTime(date, 'en', includeSeconds),
  formatDateTime: (date: Date | string) => formatDateTime(date, 'en'),
  formatNumber: (num: number, decimals = 2) => formatNumber(num, 'en', decimals),
  formatCurrency: (amount: number, currency = 'USD') => formatCurrency(amount, 'en', currency),
  formatWeight: (kg: number) => formatWeight(kg, 'en'),
};

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    return fallbackContext;
  }
  return context;
}

// HOC for class components (if needed)
export function withLanguage<P extends object>(
  Component: React.ComponentType<P & { language: LanguageContextType }>
) {
  return function WrappedComponent(props: P) {
    const language = useLanguage();
    return <Component {...props} language={language} />;
  };
}
